/**
 * workflowParallelGroupMetrics.ts — Parallel 组 sibling item 客户端聚合
 *
 * 职责：
 *     对齐 D23 §5.6；metrics_* 未填时从容器 status 统计组摘要文案。
 *
 * 链路位置：
 *     WorkflowParallelGroup → 组摘要行。
 *
 * 当前裁剪范围：
 *     仅 item scope 兄弟节点；不含 Aggregate metrics。
 */
import { isWorkflowLoopTreeActive } from './workflowLoopTreeUtils';
import type { SessionTree } from '../../types/tree';
import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';

export interface ParallelGroupMetrics {
  total: number;
  running: number;
  completed: number;
  failed: number;
}

const ACTIVE_ITEM_STATUSES = new Set<WorkflowContainerRuntimeNode['status']>([
  'running',
  'retrying',
  'pending',
  'blocked',
]);

function countMetricsFromContainerStatus(
  items: WorkflowContainerRuntimeNode[],
): ParallelGroupMetrics {
  let running = 0;
  let completed = 0;
  let failed = 0;

  for (const item of items) {
    if (ACTIVE_ITEM_STATUSES.has(item.status)) {
      running += 1;
      continue;
    }
    switch (item.status) {
      case 'completed':
        completed += 1;
        break;
      case 'failed':
        failed += 1;
        break;
      default:
        break;
    }
  }

  return {
    total: items.length,
    running,
    completed,
    failed,
  };
}

function readDisplayMetrics(items: WorkflowContainerRuntimeNode[]): ParallelGroupMetrics | null {
  let hasMetrics = false;
  let running = 0;
  let completed = 0;
  let failed = 0;
  let total = 0;

  for (const item of items) {
    const metrics = item.display;
    if (
      metrics?.metrics_total != null
      || metrics?.metrics_running != null
      || metrics?.metrics_completed != null
      || metrics?.metrics_failed != null
    ) {
      hasMetrics = true;
    }
    running += metrics?.metrics_running ?? 0;
    completed += metrics?.metrics_completed ?? 0;
    failed += metrics?.metrics_failed ?? 0;
    total += metrics?.metrics_total ?? 0;
  }

  if (!hasMetrics) return null;
  if (total <= 0) total = items.length;
  return { total, running, completed, failed };
}

/** display.metrics_* 与容器 status 双轨取 max，防 SDK 滞后导致组误折。 */
function mergeParallelGroupMetrics(
  fromDisplay: ParallelGroupMetrics,
  fromStatus: ParallelGroupMetrics,
  itemCount: number,
): ParallelGroupMetrics {
  return {
    total: Math.max(fromDisplay.total, fromStatus.total, itemCount),
    running: Math.max(fromDisplay.running, fromStatus.running),
    completed: Math.max(fromDisplay.completed, fromStatus.completed),
    failed: Math.max(fromDisplay.failed, fromStatus.failed),
  };
}

/** 从 sibling item 容器统计组 metrics（display.metrics_* 优先，status 兜底/校正）。 */
export function computeParallelGroupMetrics(
  items: WorkflowContainerRuntimeNode[],
): ParallelGroupMetrics {
  const fromStatus = countMetricsFromContainerStatus(items);
  const fromDisplay = readDisplayMetrics(items);
  if (!fromDisplay) return fromStatus;
  return mergeParallelGroupMetrics(fromDisplay, fromStatus, items.length);
}

/**
 * 原则 A · L2：组内仍有 active 工作（容器 status 或 scoped loop 未 settle）→ 不得折叠。
 * 结构帧（parallel-item-done）可能早于 loop finish，须双轨校验。
 */
export function parallelGroupHasActiveWork(
  items: WorkflowContainerRuntimeNode[],
  loopTreesBySessionId: Record<string, SessionTree> = {},
): boolean {
  const metrics = computeParallelGroupMetrics(items);
  if (metrics.running > 0) return true;
  for (const item of items) {
    const loopSessionId = item.loopSessionId;
    if (!loopSessionId) continue;
    if (isWorkflowLoopTreeActive(loopTreesBySessionId[loopSessionId])) return true;
  }
  return false;
}

export function formatParallelGroupSummary(metrics: ParallelGroupMetrics): string {
  if (metrics.running > 0) {
    const n = metrics.running;
    return `Running ${n} agent${n === 1 ? '' : 's'}…`;
  }
  if (metrics.failed > 0) {
    const ok = metrics.completed;
    return `Done (${ok} agent${ok === 1 ? '' : 's'}) · ${metrics.failed} failed`;
  }
  const n = metrics.total;
  return `Done (${n} agent${n === 1 ? '' : 's'})`;
}
